import * as prismic from "@prismicio/client";

// Same public-read repo across every stage, so the env var is an override rather than
// a requirement — the default keeps the Help section working in deployed builds where
// REACT_APP_PRISMIC_REPOSITORY_NAME is not wired into the frontend CI variables.
const repositoryName =
  process.env.REACT_APP_PRISMIC_REPOSITORY_NAME || "antaiaweb";
const endpoint = `https://${repositoryName}.cdn.prismic.io/api/v2`;

// Public, read-only repo (same instance as the marketing website) — no access token needed.
export const prismicClient = prismic.createClient(endpoint);

export function getHelpArticles() {
  return prismicClient.getAllByType("help_article", {
    orderings: [{ field: "my.help_article.order" }],
  });
}

export function getHelpArticleByUID(uid) {
  return prismicClient.getByUID("help_article", uid);
}

// The Help section now spans three page types authored in Prismic — "help_article"
// (the "Help Home" type, e.g. the Entry article), "help_guide", and "help_hub" — all
// sharing the same title/summary field shape. Search results should surface all
// three, so this fetches every document across all three types in one query rather
// than three separate getAllByType calls.
const HELP_SEARCHABLE_TYPES = ["help_article", "help_guide", "help_hub"];

export function getHelpSearchableDocuments() {
  return prismicClient.dangerouslyGetAll({
    filters: [prismic.filter.any("document.type", HELP_SEARCHABLE_TYPES)],
  });
}

/**
 * Fetches `faq` documents matching an AND/OR/NOT tag filter — used by the "faq"
 * slice's `faq_tags` (AND — every doc must have ALL of these), `must_have_at_least_one_of_these`
 * (OR — every doc must have at least one of these), and `must_not_have_any_of_these`
 * (NOT — no doc may have any of these) repeatable groups (see FaqSlice.jsx).
 * Each empty/omitted bucket is skipped, so a slice with only `faq_tags` set still
 * works as a plain AND filter.
 * @param {{ andTags?: string[], orTags?: string[], notTags?: string[] }} params
 */
export function getFaqsByTags({ andTags = [], orTags = [], notTags = [] } = {}) {
  const filters = [
    ...andTags.map((tag) => prismic.filter.any("document.tags", [tag])),
    ...(orTags.length > 0 ? [prismic.filter.any("document.tags", orTags)] : []),
    ...(notTags.length > 0 ? [prismic.filter.not("document.tags", notTags)] : []),
  ];
  return prismicClient.getAllByType("faq", filters.length > 0 ? { filters } : undefined);
}

// Every `faq` document, unfiltered — used by Help search (HelpPage.jsx) to make FAQ
// question/answer text searchable. A `faq` slice's Q&A content isn't embedded in the
// help document at all (FaqSlice.jsx resolves it live, by tag, via getFaqsByTags above),
// so search fetches the full set once and replicates that same tag filter client-side
// per help document rather than firing one getFaqsByTags call per faq slice per article.
export function getAllFaqs() {
  return prismicClient.getAllByType("faq");
}
